import {Plan} from '../types/Plan';
import {Col, Row} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCaretDown, faCaretRight} from '@fortawesome/free-solid-svg-icons';
import React, {useEffect, useRef, useState} from 'react';
import {getFormattedNumber} from '../utils/NumberUtils';
import {StromApi} from '../api';

interface TreeProps {
    data: Plan[];
    skutecnost: boolean;
    setSelected: (Plan: Plan) => void;
    idPlan: number | undefined;
}

export const Tree: React.FC<TreeProps> = ({data, skutecnost, setSelected, idPlan}) => {
    const [focus, setFocus] = useState(0);
    const [rerender, setRerender] = useState(false);
    const [tempId, setTempId] = useState<number>(-1);

    let myRef = useRef<null | HTMLDivElement>(null);

    useEffect(() => {
        if (idPlan) {
            loadPath();
        }
    }, [idPlan, skutecnost]);

    const loadPath = async () => {
        if (idPlan) {
            const pth = (await StromApi.getCestaKZaznamu(idPlan)).data;
            pth.forEach((part, index) => {
                if (index !== pth.length - 1) {
                    data.forEach(item => {
                        if (item.idPlan === part) {
                            item.collapsed = false;
                        } else if (item.idPlanOt === part) {
                            item.shown = true;
                        }
                    });
                }
            });
            setRerender(!rerender);
        }
    };

    const changeNode = (id: number, collapse: boolean) => {
        let toCollapse = data[id].subItemsCount;
        let it = id + 1;
        let collapsed = 0;
        let skip = 0;
        while (collapsed < toCollapse) {
            if (skip > 0) {
                skip += data[it].subItemsCount;
                skip--;
                it++;
                continue;
            }

            if (!data[it].collapsed) {
                toCollapse += data[it].subItemsCount;
            } else {
                skip += data[it].subItemsCount;
            }
            data[it].shown = !collapse;
            collapsed++;
            it++;
        }
        data[id].collapsed = collapse;
        setRerender(!rerender);
    };

    const goDown = () => {
        for (let i = focus + 1; i < data.length; i++) {
            if (data[i].shown) {
                setFocus(i);
                setSelected(data[i]);
                break;
            }
        }
    };

    const goUp = () => {
        for (let i = focus - 1; i > -1; i--) {
            if (data[i].shown) {
                setFocus(i);
                setSelected(data[i]);
                break;
            }
        }
    };

    const onClick = (val: number) => {
        setFocus(val);
        setSelected(data[val]);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === 'ArrowDown' && focus < data.length - 1) {
            goDown();
        } else if (event.key === 'ArrowUp' && focus > 0) {
            goUp();
        } else if (event.key === 'ArrowRight' && focus < data.length - 1) {
            if (data[focus].subItemsCount > 0 && data[focus].collapsed) {
                changeNode(focus, false);
            } else {
                goDown();
            }
        } else if (event.key === 'ArrowLeft') {
            if (data[focus].subItemsCount > 0 && !data[focus].collapsed) {
                changeNode(focus, true);
            } else if (focus > 0) {
                goUp();
            }
        }
        event.preventDefault();
        event.stopPropagation();
    };

    if (myRef.current) {
        myRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    let next = 1;
    let show = 0;
    return (
        <div style={{overflowY: 'auto', overflowX: 'hidden', maxHeight: '75vh'}}>
            {data.map((item, index) => {
                if (next < item.uroven) {
                    return null;
                }

                if (item.subItemsCount > 0) {
                    if (item.collapsed) {
                        next = item.uroven;
                    } else {
                        next = item.uroven + 1;
                    }
                }

                let backColor = show % 2 == 0 ? 'rgba(201,213,229,0.07)' : '#FFFFFF';
                if (item.idPlan === idPlan && tempId !== idPlan) {
                    setTempId(idPlan);
                    setFocus(index);
                }
                if (index === focus) {
                    backColor = 'rgba(193,215,255,0.84)';
                }

                let icon = null;
                if (item.subItemsCount > 0) {
                    icon = (
                        <FontAwesomeIcon
                            icon={faCaretDown}
                            style={{paddingTop: 2, paddingBottom: 2, paddingRight: 10, paddingLeft: 10}}
                            onClick={() => changeNode(index, true)}
                        />
                    );
                    if (item.collapsed) {
                        icon = (
                            <FontAwesomeIcon
                                icon={faCaretRight}
                                style={{paddingTop: 2, paddingBottom: 2, paddingRight: 10, paddingLeft: 10}}
                                onClick={() => changeNode(index, false)}
                            />
                        );
                    }
                }
                show++;

                const ref = index === focus ? myRef : null;

                return (
                    <div key={index} tabIndex={-1} ref={ref} onKeyDown={event => handleKeyPress(event)}>
                        <Row className="main-container class-row-font touchable" style={{backgroundColor: backColor}} onClick={() => onClick(index)}>
                            <Col xs={7} style={{paddingLeft: icon ? item.uroven * 15 : item.uroven * 15 + 15}}>
                                <div style={{display: 'flex'}}>
                                    <div>{icon}</div>
                                    <div style={{flex: 1}}>{item.nazevAkce}</div>
                                </div>
                            </Col>
                            <Col md="auto" className="class-col">
                                {skutecnost ? getFormattedNumber(item.upravenyRozpocet) : getFormattedNumber(item.schvalenyRozpocet)}
                            </Col>
                            <Col md="auto" className="class-col" style={{marginRight: 10}}>
                                {skutecnost ? getFormattedNumber(item.skutecnnost) : getFormattedNumber(item.upravenyRozpocet)}
                            </Col>
                        </Row>
                    </div>
                );
            })}
        </div>
    );
};
